import "./styles.css";
import debounce from "lodash.debounce";
import editorTheme from "./themes/EditorTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { useRef, useState, useEffect } from "react";
import { EditorState } from "lexical";
import { toRichTexts } from "./Serialize";
import { RichText } from "../../model/viewModel/richText";

const Placeholder = () => <div className="editor-placeholder">Enter text...</div>;

const editorConfig = {
  namespace: "MyEditor",
  theme: editorTheme,
  onError(error: Error) {
    throw error;
  },
};

interface IEditor {
  richTexts: RichText[];
  onChange: (richTexts: RichText[]) => void;
  assetId: string;
}

const Editor = ({ richTexts, onChange, assetId }: IEditor) => {
  const [status, setStatus] = useState<string>("");
  const richTextsRef = useRef<RichText[]>(richTexts);

  const handleDebouncedChange = () => {
    onChange(richTextsRef.current);
    setStatus("");
  };

  const debouncedFnRef = useRef<any>(debounce(handleDebouncedChange, 500));

  useEffect(() => {
    return () => {
      debouncedFnRef.current.cancel();
    };
  }, []);

  const handleChange = (e: EditorState) => {
    const richTexts = toRichTexts(e);
    if (JSON.stringify(richTexts) !== JSON.stringify(richTextsRef.current)) {
      setStatus("Updating...");
      richTextsRef.current = richTexts;
      debouncedFnRef.current();
    }
  };

  return (
    <div onKeyDown={(e) => e.stopPropagation()}>
      <LexicalComposer initialConfig={editorConfig} key={`lexical-${assetId}`}>
        <div className="editor-container">
          <ToolbarPlugin richTexts={richTexts} />
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={<ContentEditable className="editor-input" />}
              placeholder={<Placeholder />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin onChange={handleChange} />
          </div>
        </div>
      </LexicalComposer>
      <p className="m-1 editor-status">{status}</p>
    </div>
  );
};

export default Editor;
